import { Graphics, type Sprite } from 'pixi.js';
import { clamp01, lerp } from '../../shared/scalar';
import { DRAGON } from '../../sim/full/attacks/ink';
import type { PlayerState, PropState, SimState, ZoneState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, byId, lookup, setTint } from '../util';
import { ViewPool } from '../views';
import {
  type DragonPath,
  type DragonView,
  type Point,
  createDragonView,
  hideDragon,
  placeDragon,
} from './ink-dragon';
import { DRAGON_PIECES, NEEDLE_TIP, UNIT, inkColor } from './tatoue-paint';

const STEP_TICKS = 0.9;
const HISTORY = 16;
const DRAGON_SCALE = 2.2;
const FADE_TICKS = 6;
const CALM_GLOW = 0.5;
const TRACE_BEATS = 1.5;
const VANISH_BEATS = 0.75;
const TURN_PER_BEAT = 0.04;
const MANDALA = { petals: 8, inner: 0.62, petalAt: 0.5, petalLong: 0.3, petalWide: 0.13 };
const MANDALA_ALPHA = { day: 0.85, night: 0.75, beat: 0.25 };
const MANDALA_GLOW = 0.14;
const DASH = { on: 2, off: 5 };

interface Track {
  readonly xs: Float64Array;
  readonly ys: Float64Array;
  count: number;
  tick: number;
  seen: number;
  ticksLeft: number;
  color: number;
}

interface MandalaView {
  readonly glow: Sprite;
  readonly lines: Graphics;
  readonly trace: Graphics;
}

export function createInk(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const sheet = paintedLater((): Sheet<keyof typeof DRAGON_PIECES> => paintSheet(DRAGON_PIECES));
  const burster = createBurster(ctx, 24);
  const motifs = new Set<string>();
  for (const effect of ctx.skillEffects.values()) {
    if (effect.kind === 'motif') {
      motifs.add(effect.zone.id);
    }
  }
  const tracks = new Map<number, Track>();
  let dragons: ViewPool<DragonView> | undefined;
  let mandalas: ViewPool<MandalaView> | undefined;
  let image = 0;

  function dragonPool(): ViewPool<DragonView> {
    if (dragons === undefined) {
      const { shapes } = sheet.get();
      dragons = new ViewPool<DragonView>(
        () => createDragonView(layers.weapons, layers.glow, shapes, textures.halo),
        hideDragon,
      );
    }
    return dragons;
  }

  function mandalaPool(): ViewPool<MandalaView> {
    mandalas ??= new ViewPool<MandalaView>(
      () => {
        const glow = add(layers.glow, textures.halo);
        const lines = new Graphics();
        const trace = new Graphics();
        layers.zones.addChild(lines, trace);
        return { glow, lines, trace };
      },
      (view) => {
        view.glow.visible = view.lines.visible = view.trace.visible = false;
      },
    );
    return mandalas;
  }

  function trackOf(prop: PropState, owner: PlayerState, now: number, frame: Frame): Track {
    let track = tracks.get(prop.id);
    if (track === undefined) {
      track = {
        xs: new Float64Array(HISTORY),
        ys: new Float64Array(HISTORY),
        count: 0,
        tick: Number.NaN,
        seen: image,
        ticksLeft: prop.ticksLeft,
        color: 0,
      };
      record(track, prop.prevX, prop.prevY);
      tracks.set(prop.id, track);
    }
    if (track.tick !== now) {
      const gap = Number.isNaN(track.tick) ? 1 : now - track.tick;
      const lastX = track.xs[0] ?? prop.prevX;
      const lastY = track.ys[0] ?? prop.prevY;
      for (let missing = 1; missing < Math.min(gap, HISTORY); missing += 1) {
        const share = missing / gap;
        record(track, lerp(lastX, prop.x, share), lerp(lastY, prop.y, share));
      }
      record(track, prop.x, prop.y);
      track.tick = now;
    }
    track.seen = image;
    track.ticksLeft = prop.ticksLeft;
    track.color = inkColor(
      frame.palette,
      frame.palette[lookup(ctx.classTokens, owner.classId, 'class')],
      prop.rank ?? 0,
    );
    return track;
  }

  function record(track: Track, x: number, y: number): void {
    track.xs.copyWithin(1, 0, HISTORY - 1);
    track.ys.copyWithin(1, 0, HISTORY - 1);
    track.xs[0] = x;
    track.ys[0] = y;
    track.count = Math.min(HISTORY, track.count + 1);
  }

  let current: Track | undefined;
  let lag = 0;
  const path: DragonPath = (back: number, out: Point) => {
    const track = current;
    if (track === undefined) {
      return;
    }
    const at = Math.min(track.count - 1, lag + back * STEP_TICKS);
    const index = Math.floor(at);
    const next = Math.min(track.count - 1, index + 1);
    const share = at - index;
    out.x = lerp(track.xs[index] ?? 0, track.xs[next] ?? 0, share);
    out.y = lerp(track.ys[index] ?? 0, track.ys[next] ?? 0, share);
  };

  function drawDragons(state: SimState, alpha: number, frame: Frame): void {
    const pool = dragonPool();
    pool.begin();
    for (const prop of state.props ?? []) {
      const owner = prop.kind === DRAGON ? byId(state.players, prop.playerId) : undefined;
      if (owner === undefined) {
        continue;
      }
      const track = trackOf(prop, owner, state.tick, frame);
      current = track;
      lag = 1 - alpha;
      const fade = Math.min(1, (prop.ticksLeft + alpha) / FADE_TICKS);
      const glow = frame.light.haloAlpha * (frame.calm ? CALM_GLOW : 1);
      const unit = (DRAGON_SCALE * UNIT * owner.radius) / REFERENCE_RADIUS;
      placeDragon(
        pool.acquire(prop.id),
        path,
        (track.count - 1 - lag) / STEP_TICKS,
        track.color,
        unit,
        fade,
        glow,
        textures.halo,
      );
    }
    current = undefined;
    pool.end();
    for (const [id, track] of tracks) {
      if (track.seen === image) {
        continue;
      }
      if (track.ticksLeft > FADE_TICKS) {
        burster.spawn(frame, {
          shape: textures.ring,
          now: frame.now,
          duration: 5,
          x: track.xs[0] ?? 0,
          y: track.ys[0] ?? 0,
          fromRadius: 3,
          toRadius: 14,
          tint: track.color,
          peak: 0.7,
        });
      }
      tracks.delete(id);
    }
  }

  function drawMandala(
    view: MandalaView,
    zone: ZoneState,
    state: SimState,
    alpha: number,
    frame: Frame,
  ) {
    const { tempo, palette, light, calm } = frame;
    const total = zone.definition.durationBars * tempo.ticksPerBar;
    const since = (total - zone.ticksLeft + alpha) / tempo.ticksPerBeat;
    const left = (zone.ticksLeft - alpha) / tempo.ticksPerBeat;
    const drawn = Math.min(1, since / TRACE_BEATS);
    const shown = clamp01(left / VANISH_BEATS);
    const owner = byId(state.players, zone.playerId);
    const color =
      owner === undefined ? palette.or : palette[lookup(ctx.classTokens, owner.classId, 'class')];
    const { radius } = zone;
    const beat = calm ? 0 : MANDALA_ALPHA.beat * frame.pulse;
    const strength = (light.additive ? MANDALA_ALPHA.night : MANDALA_ALPHA.day) + beat;

    view.glow.visible = true;
    view.glow.position.set(zone.x, zone.y);
    view.glow.scale.set((radius * 1.15) / textures.halo.radius);
    view.glow.alpha = MANDALA_GLOW * drawn * shown * (light.haloAlpha + 1);
    setTint(view.glow, color);

    const { lines } = view;
    lines.visible = true;
    lines.position.set(zone.x, zone.y);
    lines.rotation = calm ? 0 : since * TURN_PER_BEAT;
    lines.alpha = strength * shown;
    lines.tint = color;
    lines.blendMode = view.trace.blendMode = light.additive ? 'add' : 'normal';
    lines.clear();
    paintMandala(lines, radius, drawn);

    view.trace.visible = drawn < 1 && owner !== undefined;
    if (owner !== undefined && view.trace.visible) {
      const x = lerp(owner.prevX, owner.x, alpha);
      const y = lerp(owner.prevY, owner.y, alpha);
      const facing = Math.atan2(owner.aim.y, owner.aim.x);
      const unit = (UNIT * owner.radius) / REFERENCE_RADIUS;
      const cos = Math.cos(facing);
      const sin = Math.sin(facing);
      const tipX = x + (NEEDLE_TIP.along * cos - NEEDLE_TIP.across * sin) * unit;
      const tipY = y + (NEEDLE_TIP.along * sin + NEEDLE_TIP.across * cos) * unit;
      const angle = lines.rotation + drawn * TAU;
      const toX = zone.x + Math.cos(angle) * radius;
      const toY = zone.y + Math.sin(angle) * radius;
      view.trace.alpha = 0.7 * strength;
      view.trace.tint = color;
      view.trace.clear();
      dashes(view.trace, tipX, tipY, toX, toY, radius / 50);
    }
  }

  return {
    reset(): void {
      tracks.clear();
      burster.bursts.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      image += 1;
      if (state.props !== undefined || dragons !== undefined) {
        drawDragons(state, alpha, frame);
      }
      if (state.zones !== undefined || mandalas !== undefined) {
        const pool = mandalaPool();
        pool.begin();
        for (const zone of state.zones ?? []) {
          if (motifs.has(zone.definition.id)) {
            drawMandala(pool.acquire(zone.id), zone, state, alpha, frame);
          }
        }
        pool.end();
      }
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      sheet.painted?.source.destroy();
    },
  };
}

function paintMandala(graphics: Graphics, radius: number, drawn: number): void {
  const width = radius / 50;
  const end = drawn * TAU;
  graphics.arc(0, 0, radius, 0, end).stroke({ width: 1.6 * width, color: 0xffffff });
  graphics
    .arc(0, 0, radius * MANDALA.inner, 0, end)
    .stroke({ width: 1.1 * width, color: 0xffffff });
  const petals = Math.floor(drawn * MANDALA.petals + 0.001);
  for (let petal = 0; petal < petals; petal += 1) {
    const angle = (petal * TAU) / MANDALA.petals;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const long = radius * MANDALA.petalLong;
    const wide = radius * MANDALA.petalWide;
    const at = radius * MANDALA.petalAt;
    graphics.moveTo(at * cos + long * cos, at * sin + long * sin);
    for (let step = 1; step <= 24; step += 1) {
      const turn = (step * TAU) / 24;
      const along = at + long * Math.cos(turn);
      const across = wide * Math.sin(turn);
      graphics.lineTo(along * cos - across * sin, along * sin + across * cos);
    }
    graphics.stroke({ width: 1.1 * width, color: 0xffffff });
    graphics.circle(radius * 0.82 * cos, radius * 0.82 * sin, 2 * width).fill({ color: 0xffffff });
  }
  if (drawn < 1) {
    return;
  }
  for (let spoke = 0; spoke < MANDALA.petals; spoke += 1) {
    const angle = ((spoke + 0.5) * TAU) / MANDALA.petals;
    graphics
      .moveTo(Math.cos(angle) * radius * MANDALA.inner, Math.sin(angle) * radius * MANDALA.inner)
      .lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
  }
  graphics.stroke({ width: 0.8 * width, color: 0xffffff });
  graphics.circle(0, 0, radius * 0.18).stroke({ width: 1.1 * width, color: 0xffffff });
}

function dashes(
  graphics: Graphics,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  width: number,
) {
  const length = Math.hypot(toX - fromX, toY - fromY);
  if (length === 0) {
    return;
  }
  const dx = (toX - fromX) / length;
  const dy = (toY - fromY) / length;
  const on = DASH.on * width;
  const period = (DASH.on + DASH.off) * width;
  for (let at = 0; at < length; at += period) {
    const stop = Math.min(length, at + on);
    graphics.moveTo(fromX + dx * at, fromY + dy * at).lineTo(fromX + dx * stop, fromY + dy * stop);
  }
  graphics.stroke({ width: 1.4 * width, color: 0xffffff, cap: 'round' });
}
